import React, { useState, useEffect } from 'react';
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Row, Col, Select, Button, Input, Switch,
  Checkbox, notification, Spin, Modal, Form,
  Typography, Tooltip,
} from 'antd';
import {
  useNavigate, useLocation, useParams,
} from 'react-router-dom';
import {
  get, put, post,
  RequestWrapper, ResponseWrapper, isSuperAgentError,
} from '../../../dataSource/requests';
import { removeUserFromStorage } from '../../../utils/users';
import './ManageRole.scss';
import { SessionExpiredException } from '../../../utils/errors';

const ManageRole = (): JSX.Element => {
  const [formInstance] = Form.useForm();

  const navigate = useNavigate();

  const locationObject = useLocation();

  const urlParametersObject = useParams();

  const [editing] = useState<boolean>(locationObject.pathname.match(/roles\/[\w-]*\/edit/) != null);

  const [loading, setLoading] = useState<boolean>(true);

  // pending, ready, finished
  const [permissionsStatus, setPermissionsStatus] = useState<string>('pending');

  const [propID] = useState(urlParametersObject.id || undefined);

  const [newType, setNewType] = useState('COOPERCITRUS');

  const [groupedPermissions, setGroupedPermissions] = useState({
    types: [],
  });

  const onChangePermissionGroup = (checkedList, category): void => {
    const groupedPermissionsClone = { ...groupedPermissions };

    const editCategory = groupedPermissionsClone
      .types
      .find((type) => type.type === newType)
      .categories
      .find((cat) => cat.category === category.category);

    editCategory.checkAll = checkedList.length === editCategory.permissions.length;
    editCategory.indeterminate = !!checkedList.length
      && checkedList.length < editCategory.permissions.length;

    editCategory.checkedPermissions = checkedList.map(
      (checkedElement) => editCategory.permissions.find(
        (perm) => perm.feature === checkedElement,
      ) || undefined,
    );

    editCategory.permissions.forEach((eachPermission) => {
      eachPermission.checked = checkedList.includes(eachPermission.feature);
    });

    setGroupedPermissions(groupedPermissionsClone);
  };

  const onCheckAllChange = (eventt, category): void => {
    const groupedPermissionsClone = { ...groupedPermissions };
  
    const editCategory = groupedPermissionsClone
      .types
      .find((type) => type.type === newType)
      .categories
      .find((cat) => cat.category === category.category);
  
    const isChecked = eventt.target.checked;
  
    editCategory.checkAll = isChecked;
    editCategory.indeterminate = false;
  
    // Marca todas as permissões, exceto "Logar Como"
    editCategory.checkedPermissions = isChecked
      ? editCategory.permissions.filter((perm) => perm.feature !== 'Logar Como')
      : [];
  
    // Marca individualmente o .checked (também excluindo "Logar Como")
    editCategory.permissions.forEach((eachPermission) => {
      eachPermission.checked = isChecked && eachPermission.feature !== 'Logar Como';
    });
  
    setGroupedPermissions(groupedPermissionsClone);
  };
  

  const onChangeRoleType = (newTypeParameter): void => {
    setNewType(newTypeParameter);
  };

  const setStatusTitle = (): string => {
    switch (true) {
      case (
        formInstance.getFieldValue('defaultRole') === true
      ):
        return 'Não é possivel inativar um perfil padrão';

      default:
        return 'Status do perfil';
    }
  };

  const setDefaultRoleTitle = (): string => {
    switch (true) {
      case (
        editing
        && (formInstance.getFieldValue('defaultRole') === true)
      ):
        return 'Torne outro perfil padrao para desmarcar';

      case (
        formInstance.getFieldValue('status') === 'DEACTIVATED'
      ):
        return 'Não é possivel marcar um perfil inativo como padrão';

      default:
        return 'Perfil Padrão';
    }
  };

  const handleKnownErrors = (response): void => {
    let notificationMessage = 'Houve um erro ao executar sua solicitação, tente novamente por favor';

    switch (true) {
      case (response.body.code === -3000):
        notificationMessage = 'Perfil não encontrado';
        navigate('/roles/list');
        break;

      case (response.body.code === -3005):
        notificationMessage = 'Este nome de perfil já está em uso';
        break;

      case (response.body.code === -4001):
        notificationMessage = 'Sessão expirada. Realize o login novamente, por favor';
        removeUserFromStorage();
        navigate('/login');
        break;

      default:
        break;
    }

    notification.error({
      message: 'Erro',
      description: notificationMessage,
    });
  };

  const handleSubmit = async (values): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!values) {
      try {
        values = await formInstance.validateFields();
      } catch (error) {
        console.log(error);

        notification.warning({
          message: 'Atenção',
          description: 'Informações inválidas, verifique e tente novamente por favor',
        });

        return;
      }
    }

    if (editing) {
      const editPermissions = [];

      groupedPermissions
        .types
        .find((type) => type.type === values.type)
        .categories
        .forEach((eachCat) => {
          eachCat.checkedPermissions.forEach((eachPerm) => {
            editPermissions.push({ feature: eachPerm.feature, enable: true });
          });
        });

      setLoading(true);

      const requestReference: RequestWrapper = put({
        url: '/role/edit',

        params: {
          defaultRole: values.defaultRole,
          id: propID,
          name: values.name,
          status: values.status,
          permissions: editPermissions,
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          notification.error({
            message: 'Erro',
            description: 'Houve um erro ao editar o perfil, tente novamente por favor',
          });
        }

        setLoading(false);
        return;
      }

      if (!response.error && !response.body.error) {
        notification.success({
          message: 'Sucesso',
          description: 'O Perfil foi editado',
        });

        navigate('/roles/list');
        return;
      }

      handleKnownErrors(response);

      setLoading(false);
    } else {
      const editPermissions = [];

      groupedPermissions
        .types
        .find((type) => type.type === values.type)
        .categories
        .forEach((eachCat) => {
          eachCat.checkedPermissions.forEach((eachPerm) => {
            editPermissions.push({ feature: eachPerm.feature, enable: true });
          });
        });

      setLoading(true);

      const requestReference: RequestWrapper = post({
        url: '/role/create',

        params: {
          defaultRole: values.defaultRole,
          name: values.name,
          status: values.status,
          type: values.type,
          permissions: editPermissions,
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          notification.error({
            message: 'Erro',
            description: 'Houve um erro ao criar o perfil, tente novamente por favor',
          });
        }

        setLoading(false);
        return;
      }

      if (!response.error && !response.body.error) {
        notification.success({
          message: 'Sucesso',
          description: 'O Perfil foi criado',
        });

        navigate('/roles/list');

        return;
      }

      handleKnownErrors(response);
    }
  };

  useEffect(() => {
    const loadTypesWithPermissions = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/role/findTypesWithPermissions',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }

        if (!(error instanceof SessionExpiredException)) {
          notification.error({
            message: 'Erro',
            description: 'Houve um problema ao carregar os tipos de perfis. Por favor tente novamente.',
          });
        }

        console.log(error);
        return;
      }

      const newTypes = new Set();
      const newGroupedPermissions = {
        types: [],
      };

      if (response.error || response.body.error) {
        handleKnownErrors(response);
      } else {
        response.body.types.forEach((typeValue) => {
          newTypes.add(typeValue.type);
        });

        newTypes.forEach((typeSetValue) => {
          newGroupedPermissions.types.push({ type: typeSetValue, categories: [] });
        });

        response.body.types.forEach((typeValue) => {
          const categories = new Set();

          typeValue.permissions.forEach((permValue) => {
            categories.add(permValue.category);
          });

          newGroupedPermissions.types.filter((t) => t.type === typeValue.type)
            .forEach((gptValue) => {
              categories.forEach((cat) => {
                const newCat = {
                  category: cat,
                  indeterminate: false,
                  checkAll: false,
                  permissions: [],
                  checkedPermissions: [],
                };

                typeValue.permissions.filter((permFilter) => permFilter.category === cat)
                  .forEach((permEach) => {
                    newCat.permissions.push({ feature: permEach.feature, checked: false });
                  });

                gptValue.categories.push(newCat);
              });

              return gptValue;
            });
        });
        setGroupedPermissions(newGroupedPermissions);
        setLoading((previousLoading) => (editing ? previousLoading : false));
      }

      setPermissionsStatus('ready');
    };

    loadTypesWithPermissions();

    return () => {
      setLoading(false);
    };
  }, []);

  useEffect(() => {
    const loadEditingData = async (): Promise<void> => {
      let responseRole: ResponseWrapper | undefined;
      let responseRoleError: ResponseWrapper | undefined;
      let genericErrorRole: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: `/role/find/${propID}`,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        responseRole = await requestReference;
      } catch (error) {
        if (!(error instanceof SessionExpiredException)) {
          notification.error({
            message: 'Erro',
            description: 'Não foi possivel encontrar os dados do perfil, tente novamente por favor',
          });
        }

        formInstance.validateFields();
        console.log(error);
        return;
      }

      if (!responseRole.error && !responseRole.body.error) {
        const groupedPermissionsEdit = { ...groupedPermissions };

        groupedPermissionsEdit
          .types
          .find((type) => type.type === responseRole.body.role.type)
          .categories
          .forEach((eachCategory) => {
            const editPermsCat = responseRole.body.role.permissions.filter(
              (perm) => perm.category === eachCategory.category,
            );

            eachCategory.indeterminate = !!editPermsCat.length
              && (editPermsCat.length < eachCategory.permissions.length);

            eachCategory.checkAll = editPermsCat
              .length === eachCategory.permissions.length;

            eachCategory.checkedPermissions = editPermsCat.map((mapPermission) => {
              mapPermission.checked = true;
              return mapPermission;
            });

            eachCategory.permissions.forEach((eachPermission) => {
              eachPermission.checked = false;

              if (editPermsCat.map(
                (mapEachPermission) => mapEachPermission.feature,
              ).includes(eachPermission.feature)) {
                eachPermission.checked = true;
              }
            });
          });

        setGroupedPermissions(groupedPermissionsEdit);
        // setNewName(responseRole.body.role.name);
        setNewType(responseRole.body.role.type);
        // setNewStatus(responseRole.body.role.status);
        // setNewDefaultRole(responseRole.body.role.defaultRole);
        formInstance.setFieldsValue({
          name: responseRole.body.role.name,
          type: responseRole.body.role.type,
          status: responseRole.body.role.status,
          defaultRole: responseRole.body.role.defaultRole,
        });
        setLoading(false);
      } else {
        handleKnownErrors(responseRole);
      }

      formInstance.validateFields();
      setPermissionsStatus('finished');
    };

    if (editing && propID && permissionsStatus === 'ready') {
      loadEditingData();
      formInstance.validateFields();
    }
  }, [permissionsStatus]);

  return (
    <div className="ManageRole">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={16} className="menu-row">
          <Col xs={{ span: 24 }} lg={{ span: 18 }}>
            <span className="title">
              {editing ? 'Editar perfil' : 'Criar novo perfil'}
            </span>
          </Col>
          <Col xs={{ span: 24 }} lg={{ span: 3 }}>
            <Button
              className="back-button"
              onClick={
                (): void => {
                  if (formInstance.isFieldsTouched()) {
                    Modal.confirm({
                      title: 'Atenção',
                      content: 'Todas as alterações serão perdidas. Deseja continuar?',
                      okText: 'Sim, continuar',
                      cancelText: 'Não, cancelar',
                      autoFocusButton: null,
                      icon: (<WarningOutlined />),

                      onOk: () => {
                        navigate('/roles/list');
                      },
                    });
                  } else {
                    navigate('/roles/list');
                  }
                }
              }
            >
              Voltar
            </Button>
          </Col>

          <Col xs={{ span: 24 }} lg={{ span: 3 }}>
            <Button
              onClick={
                (): void => {
                  formInstance.submit();
                }
              }
              className="save-button"
            >
              Salvar
            </Button>
          </Col>
        </Row>
        <div className="content-wrapper">
          <Form
            scrollToFirstError
            onFinish={handleSubmit}
            form={formInstance}
            layout="vertical"
            initialValues={{
              status: 'ACTIVATED',
              defaultRole: false,
            }}
          >
            <Row gutter={[32, 32]}>
              <Col sm={12} lg={6}>
                <Form.Item
                  label={<span className="form-field-label">Nome do perfil</span>}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: 'Por favor insira um nome',
                    },
                    {
                      max: 80,
                      message: 'O nome deve conter no máximo 80 caracteres',
                    },
                  ]}
                >
                  <Input placeholder="Digite o nome do perfil" />
                </Form.Item>
              </Col>
              <Col sm={12} lg={6}>
                <Form.Item
                  label={<span className="form-field-label">Tipo do perfil</span>}
                  name="type"
                  initialValue={newType}
                  rules={[
                    {
                      required: true,
                      message: 'Por favor selecione um tipo',
                    },
                  ]}
                >
                  <Select
                    disabled={editing}
                    placeholder="Escolha o tipo de perfil"
                    onChange={onChangeRoleType}
                  >
                    <Select.Option value="COOPERATIVE">Cooperado</Select.Option>
                    <Select.Option value="COOPERCITRUS">Coopercitrus</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col sm={12} lg={6}>
                <Form.Item
                  label={<span className="form-field-label">Status</span>}
                  shouldUpdate={
                    (previousValues, currentValues): boolean => previousValues
                      .defaultRole !== currentValues.defaultRole
                  }
                >
                  {
                    (): JSX.Element => {
                      return (
                        <Form.Item
                          name="status"
                          noStyle
                          rules={[
                            {
                              required: true,
                              message: 'Por favor selecione um status',
                            },
                          ]}
                        >
                          <Select
                            disabled={
                              formInstance.getFieldValue('defaultRole') === true
                            }
                            title={setStatusTitle()}
                          >
                            <Select.Option value="ACTIVATED">Ativo</Select.Option>
                            <Select.Option value="DEACTIVATED">Inativo</Select.Option>
                          </Select>
                        </Form.Item>
                      );
                    }
                  }
                </Form.Item>
              </Col>
              <Col sm={12} lg={6}>
                <Form.Item
                  label={<span className="form-field-label">Perfil padrão</span>}
                  shouldUpdate={
                    (previousValues, currentValues): boolean => previousValues
                      .status !== currentValues.status
                  }
                >
                  {
                    (): JSX.Element => {
                      return (
                        <Form.Item
                          name="defaultRole"
                          noStyle
                          valuePropName="checked"
                        >
                          <Switch
                            size="small"
                            disabled={
                              formInstance.getFieldValue('status') === 'DEACTIVATED'
                            }
                            title={setDefaultRoleTitle()}
                          />
                        </Form.Item>
                      );
                    }
                  }
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Typography.Title
                  level={5}
                  className="form-field-label"
                >
                  Permissões
                </Typography.Title>
              </Col>
            </Row>
            <Row gutter={[32, 32]}>
              <Form.Item
                label={<span className="form-field-label">Permissoes</span>}
                className="permissions"
                shouldUpdate={
                  (previousValues, currentValues): boolean => previousValues
                    .type !== currentValues.type
                }
                noStyle
              >
                {
                  () => {
                    const permissions = (groupedPermissions)
                      ? groupedPermissions.types.filter(
                        (typeElement) => typeElement.type === newType,
                      )
                      : [];

                    return (permissions.length && permissions[0].categories.length)
                      ? permissions[0].categories.map((category) => {
                        let features = [];

                        features = category.permissions.map((perm) => ({
                          label: perm.feature,
                          value: perm.feature,
                        }));

                        return (
                          <Col
                            sm={12}
                            lg={6}
                            key={category.category}
                            style={{ paddingRight: '16px', paddingLeft: '16px' }}
                          >
                            <Row>
                              <Col xs={24}>
                                <Checkbox
                                  id="allCabecalhos"
                                  indeterminate={category.indeterminate}
                                  onChange={
                                    (eventt): void => {
                                      onCheckAllChange(eventt, category);
                                    }
                                  }
                                  checked={category.checkAll}
                                  className="category-checkbox-header"
                                >
                                  {category.category}
                                </Checkbox>
                              </Col>
                            </Row>
                            <Row>
                              <Col xs={24}>
                                <Checkbox.Group
                                  name={`permissions[${category.category}]`}
                                  value={
                                    category.checkedPermissions.map(
                                      (permShow) => permShow.feature,
                                    )
                                  }
                                  className="features"
                                  onChange={
                                    (eventt): void => {
                                      onChangePermissionGroup(eventt, category);
                                    }
                                  }
                                >
                                  <Row>
                                    {
                                      features.map((feat, index) => (
                                        <Col
                                          key={feat.value}
                                          span={24}
                                        >
                                          <Checkbox
                                              className="feature-checkbox ant-checkbox-group-item"
                                              key={`${feat.value} ${index}`}
                                              value={feat.value}
                                              disabled={feat.value === 'Logar Como'}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                {feat.value}
                                                {feat.value === 'Listagem de importação de resultados' && (
                                                  <span style={{ marginLeft: '.7rem' }}>
                                                    <Tooltip
                                                      placement="left"
                                                      title="É necessário a permissão de solicitar novo serviço ou orçamento."
                                                    >
                                                      <InfoCircleOutlined />
                                                    </Tooltip>
                                                  </span>
                                                )}
                                                {feat.value === 'Logar Como' && (
                                                  <span style={{ marginLeft: '.7rem' }}>
                                                    <Tooltip
                                                      placement="left"
                                                      title="Usado para o Administrador logar no aplicativo móvel como um cooperado."
                                                    >
                                                      <InfoCircleOutlined />
                                                    </Tooltip>
                                                  </span>
                                                )}
                                              </div>
                                            </Checkbox>
                                        </Col>
                                      ))
                                    }
                                  </Row>
                                </Checkbox.Group>
                              </Col>
                            </Row>
                          </Col>
                        );
                      }) : null;
                  }
                }
              </Form.Item>
            </Row>
          </Form>
        </div>
      </Spin>
    </div>
  );
};

ManageRole.propTypes = {};
ManageRole.defaultProps = {};

export default ManageRole;
